import React, { useState, useContext, useMemo, DragEvent, MouseEvent } from 'react';

import cl from 'classnames';
import { darken } from 'polished';
import { useDrag, useDragLayer } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useSelector } from 'react-redux';

import { useEventOption } from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/Event/functions';
import Text from 'components/shared/Typography/Text';
import { itineraryScheduleTypeSelector } from 'store/itinerariesItem/selectors';
import { getEventPlankTitle, checkIsExpandableEvent, setFullEventPlankBorderColor } from 'utils/itineraryDetails';

import { DragAndDropContext } from '../../../contexts';
import { DragTypeEnum } from '../../../contexts/drag-and-drop';
import { getDayNumberFromCursor } from '../../../utils';
import { checkIsEventInitialInPeriod } from '../../../utils/days-generator/helpers';
import * as constants from './constants';
import { EventPlankLayout } from './EventPlankLayout';
import s from './index.module.css';
import * as types from './types';
import { getUpdatedSelectedEvents } from './utils';

function EventPlank({ event, calendarDay, period }: types.EventPlankProps) {
  const {
    selectedEvents,
    updateSelectedEvents,
    setTempCalendarEvents,
    setDraggingStartDay,
    onDrop,
    onMultiDrop,
    tempCalendarEvents,
  } = useContext(DragAndDropContext.context);
  const organizedBy = useSelector(itineraryScheduleTypeSelector);
  const { handleEdit } = useEventOption({ id: event.id });
  const isExpandableEvent = checkIsExpandableEvent(event);
  const isTemp = useMemo(() => Boolean(tempCalendarEvents.find((e) => e.id === event.id)), [tempCalendarEvents, event]);
  const isSelected = useMemo(() => Boolean(selectedEvents.find((e) => e.id === event.id)), [selectedEvents, event]);
  const [draggingDay, setDraggingDay] = useState<number | null>(null);
  const [isDraggingStarted, setIsDraggingStarted] = useState<boolean>(false);
  const plankRef = React.useRef<HTMLDivElement>(null);

  const dragLayer = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    isDragging: monitor.isDragging(),
  }));

  const [{ isDragging }, dragRef, preview] = useDrag<
    Pick<types.CalendarEvent, 'id'>,
    types.TCalendarDay,
    { isDragging: boolean }
  >(
    () => ({
      type: constants.dragAndDrop.EVENT_PLANK_DEFAULT,
      item: { id: event.id, event, calendarDay, period, width: plankRef.current?.offsetWidth },
      end: (_, monitor) => {
        const dropResult = monitor.getDropResult();
        setIsDraggingStarted(false);

        if (!dropResult) {
          setTempCalendarEvents([]);
          return;
        }

        if (isSelected) {
          const updatedSelectedEvents = getUpdatedSelectedEvents(selectedEvents, draggingDay!, dropResult.dayNumber);

          onMultiDrop(updatedSelectedEvents);
          return;
        }
        onDrop({ id: event.id }, { dayNumber: dropResult.dayNumber }, DragTypeEnum.move);
        updateSelectedEvents([]);
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [selectedEvents, draggingDay, period, event, calendarDay],
  );

  /**
   * Disable drag preview for our custom preview
   * We need it cause we have multiple drag
   * Custom preview could be found in DragLayer component
   */
  React.useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  const [{ isExtending }, extendRef] = useDrag<
    Pick<types.CalendarEvent, 'id'>,
    types.TCalendarDay,
    { isExtending: boolean }
  >(
    () => ({
      type: constants.dragAndDrop.EVENT_PLANK_EXTENDS,
      item: { id: event.id },
      end: (_, monitor) => {
        const dropResult = monitor.getDropResult();
        setIsDraggingStarted(false);

        if (!dropResult) {
          setTempCalendarEvents([]);
          return;
        }

        onDrop({ id: event.id }, { dayNumber: dropResult.dayNumber }, DragTypeEnum.extend);
      },
      collect: (monitor) => ({
        isExtending: !!monitor.isDragging(),
      }),
    }),
    [event],
  );

  const numberOfDays = useMemo(() => {
    const daysWithinInterval = period.filter((day) => {
      const startDayNumber = event.daysRange.start;
      const endDayNumber = event.daysRange.end || startDayNumber;

      return day.dayNumber >= startDayNumber && day.dayNumber <= endDayNumber;
    });

    return daysWithinInterval.length;
  }, [event, period]);

  const isHiddenEvent = useMemo(
    () => !checkIsEventInitialInPeriod(event, { calendarDay, period }),
    [calendarDay, event, period],
  );

  const width = useMemo(() => {
    if (isHiddenEvent) return '100%';

    const widthPercent = 100 * numberOfDays;
    const offset = 22 * (numberOfDays - 1);
    const borderOffset = 1 * (numberOfDays - 1);

    return `calc(${widthPercent}% + ${offset}px + ${borderOffset}px)`;
  }, [isHiddenEvent, numberOfDays]);

  const opacity = useMemo(() => {
    if (tempCalendarEvents.length > 0) {
      if (isDragging) return '0.5';
      if (dragLayer.item?.id === event.id) return '0.5';
    }

    if (dragLayer.isDragging && isSelected) {
      return '0.5';
    }

    return '1';
  }, [dragLayer.item?.id, event.id, isDragging, tempCalendarEvents, dragLayer.isDragging, isSelected]);

  const eventType = useMemo(() => event.type, [event.type]);
  const eventTitle = useMemo(() => getEventPlankTitle(event), [event]);

  const eventColor = useMemo(() => {
    return constants.COLOR_MAP[eventType];
  }, [eventType]);

  const eventBorderColor = useMemo(() => darken(0.5, eventColor), [eventColor]);
  const pointerEvents =
    isExtending ||
    isDraggingStarted ||
    (dragLayer.isDragging && dragLayer.itemType === constants.dragAndDrop.EVENT_PLANK_EXTENDS) ||
    (dragLayer.isDragging &&
      dragLayer.itemType === constants.dragAndDrop.EVENT_PLANK_DEFAULT &&
      dragLayer.item.id !== event.id)
      ? 'none'
      : 'auto';
  const extendingPlankPointerEvents =
    (dragLayer.isDragging && dragLayer.itemType === constants.dragAndDrop.EVENT_PLANK_DEFAULT) ||
    (dragLayer.isDragging &&
      dragLayer.itemType === constants.dragAndDrop.EVENT_PLANK_EXTENDS &&
      dragLayer.item.id !== event.id) ||
    isSelected ||
    isDraggingStarted
      ? 'none'
      : 'auto';

  const handleDragStart = (e: DragEvent<HTMLDivElement>) => {
    if (!isSelected) {
      updateSelectedEvents([]);
    }

    setTimeout(() => {
      setIsDraggingStarted(true);
    }, 0);

    const dayNumber = getDayNumberFromCursor(e.clientX, e.clientY);

    if (dayNumber) {
      setDraggingStartDay(dayNumber);
      setDraggingDay(dayNumber);
    }
  };

  const handleExtendingStart = () => {
    updateSelectedEvents([]);

    setTimeout(() => {
      setIsDraggingStarted(true);
    }, 0);
  };

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (e.shiftKey) {
      e.preventDefault();

      if (isSelected) {
        const newSelectedEventsForDragging = selectedEvents.filter((e) => e.id !== event.id);
        updateSelectedEvents(newSelectedEventsForDragging);
        return;
      }

      updateSelectedEvents([...selectedEvents, event]);
      return;
    }

    handleEdit().catch((err) => err);
  };

  if (isHiddenEvent) {
    return (
      <div className={cl(s.plank, s['plank-hidden'])} style={{ opacity }}>
        <div className={s['plank-content']}>
          <Text size="medium">{eventTitle}</Text>
        </div>
      </div>
    );
  }

  return (
    <EventPlankLayout
      event={event}
      calendarDay={calendarDay}
      period={period}
      isSelected={isSelected}
      isTemp={isTemp}
      organizedBy={organizedBy}
      expandableChild={
        isExpandableEvent && (
          <div
            ref={extendRef}
            onDragStart={handleExtendingStart}
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: width,
              zIndex: 1,
              height: '100%',
              width: 5,
              background: 'transparent',
              cursor: 'col-resize',
              pointerEvents: extendingPlankPointerEvents,
            }}
          ></div>
        )
      }
      wrapperProps={{
        ref: plankRef,
        className: s.plank,
        style: { opacity },
        onClick: handleClick,
        onMouseOver: () => setFullEventPlankBorderColor(event.id, eventBorderColor),
        onMouseOut: () => setFullEventPlankBorderColor(event.id, 'transparent'),
      }}
      planksStyles={{
        width,
        pointerEvents,
      }}
      plankProps={{
        'data-plank-tail-id': event.id,
        'data-plank-start-date': event.datesRange.start,
        ref: dragRef,
        onDragStart: handleDragStart,
      }}
    />
  );
}

export default EventPlank;
